/**
 * Cooling System draft/name/clone bindings for the descriptor engine (#997 Story
 * 6.6, mirror of `largeUpsEngineBindings.ts`).
 *
 * The engine's generic factories (`descriptorCreateDraft` /
 * `descriptorEnsureNameForCreate` / `descriptorCloneForCreate`) don't match the
 * bespoke Cooling CREATE-flow semantics: bespoke drafts carry
 * `DEFAULT_COOLING_SYSTEM_CONFIGURATION` (status pre-filled to 'new'), auto-names
 * are serial hashes (`generateAutoCoolingSystemName`), and duplicates take a fresh
 * name while dropping id/metadata. Per the story ADR (parity by reuse), each
 * binding here delegates to the PROVEN bespoke util and only converts shapes at
 * the engine boundary (`CoolingSystemConfiguration` ↔ `DescriptorInstance` via the
 * Story 6.6 converters).
 */

import {
  cloneCoolingSystemForCreate,
  createDraftCoolingSystemConfiguration,
} from '../../../../components/coolingsystems/coolingSystemListUtils';
import {
  coolingSystemToDescriptorInstance,
  descriptorInstanceToCoolingSystem,
} from '../../../../services/descriptorEditSessionFlush';
import { generateAutoCoolingSystemName } from '../../../../utils/coolingSystemAutoName';
import { COOLING_SYSTEM_DESCRIPTOR } from '../../../sections/coolingSystem.descriptor';

import type { UpdateCoolingSystemConfigurationInput } from '../../../../types/coolingSystem';
import type {
  DescriptorInstance,
  DescriptorInstancePatch,
} from '../../../sections/descriptorEngineAdapter';

function patchToCoolingSystemInput(
  data: DescriptorInstancePatch,
): UpdateCoolingSystemConfigurationInput {
  const { instanceName, instanceStatus, ...fields } = data;
  return {
    ...fields,
    name: typeof instanceName === 'string' ? instanceName : undefined,
    status: instanceStatus as UpdateCoolingSystemConfigurationInput['status'],
  } as UpdateCoolingSystemConfigurationInput;
}

/** Bespoke draft factory: DEFAULT_COOLING_SYSTEM_CONFIGURATION + normalizeStatus. */
export function coolingSystemCreateDraft(
  data: DescriptorInstancePatch,
  displayOrder: number,
  tempId: number,
): DescriptorInstance {
  const unit = createDraftCoolingSystemConfiguration(
    patchToCoolingSystemInput(data),
    displayOrder,
    tempId,
  );
  return coolingSystemToDescriptorInstance(unit) as DescriptorInstance;
}

/** Bespoke auto-name: serial hash, mirrors ensureCoolingSystemNameForCreate. */
export function coolingSystemEnsureNameForCreate(
  data: DescriptorInstancePatch,
  tempId: number,
  takenNames: Iterable<string | undefined>,
): DescriptorInstancePatch {
  if (typeof data.instanceName === 'string' && data.instanceName.trim()) {
    return data;
  }
  return {
    ...data,
    instanceName: generateAutoCoolingSystemName(tempId, takenNames),
  };
}

/** Bespoke duplicate: fresh name, keeps status, drops id/metadata. */
export function coolingSystemCloneForCreate(
  source: DescriptorInstance,
  takenNames: Iterable<string | undefined>,
  tempId: number,
): Partial<DescriptorInstance> {
  const sourceUnit = descriptorInstanceToCoolingSystem(
    source,
    0,
    COOLING_SYSTEM_DESCRIPTOR,
  );
  const cloned = cloneCoolingSystemForCreate(sourceUnit, takenNames, tempId);
  return coolingSystemToDescriptorInstance({
    ...cloned,
    id: tempId,
  } as Parameters<typeof coolingSystemToDescriptorInstance>[0]) as Partial<DescriptorInstance>;
}
